import React, { useEffect, useRef, useState } from 'react';
import {
  hämtaKunder, hämtaMoten, hämtaMote, uppdateraMote,
  hämtaPaminnelser, skapaPaminnelse, uppdateraPaminnelse, taBortPaminnelse,
  Kund, Mote, Paminnelse, AktivitetsTyp,
} from '../api/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TEMA, useMobil } from '../theme';

const KOMMANDE_DAGAR = 14;
const KOMMANDE_VISA_STANDARD = 4;
const MAX_KUNDFÖRSLAG = 6;

type DatumVal = 'idag' | 'imorgon' | 'valj';

const DATUMKNAPPAR: { val: DatumVal; etikett: string }[] = [
  { val: 'idag', etikett: 'I dag' },
  { val: 'imorgon', etikett: 'I morgon' },
  { val: 'valj', etikett: 'Välj datum' },
];

function formateraLokalt(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function dagensDatumLokalt(): string {
  return formateraLokalt(new Date());
}

function datumPlusDagar(datum: string, antal: number): string {
  const d = new Date(datum + 'T00:00:00');
  d.setDate(d.getDate() + antal);
  return formateraLokalt(d);
}

function dagarMellan(datum: string, idag: string): number {
  const a = new Date(datum + 'T00:00:00');
  const b = new Date(idag + 'T00:00:00');
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
}

function närEtikett(nästaKontakt: string, idag: string): { text: string; overdue: boolean; today: boolean } {
  const dagar = dagarMellan(nästaKontakt, idag);
  if (dagar > 0) return { text: dagar === 1 ? '1 dag sen' : `${dagar} dagar sen`, overdue: true, today: false };
  if (dagar === 0) return { text: 'Idag', overdue: false, today: true };
  if (dagar === -1) return { text: 'Imorgon', overdue: false, today: false };
  return { text: fulltKortDatum(nästaKontakt), overdue: false, today: false };
}

// "fre 18 sep" — veckodag, dag och månad så att månadsskiften blir tydliga.
function fulltKortDatum(datum: string): string {
  const d = new Date(datum + 'T00:00:00');
  return d
    .toLocaleDateString('sv-SE', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(/\./g, '');
}

const AKTIVITETS_ETIKETT: Partial<Record<AktivitetsTyp, string>> = {
  paminnelse: 'påminnelse',
  möte: 'uppföljning',
  epost: 'uppföljning',
  samtal: 'uppföljning',
  anteckning: 'uppföljning',
};

// Att göra blandar två källor: uppföljningar från möten och egna påminnelser.
// De normaliseras till en gemensam form så att listan kan sorteras och ritas lika.
type Punkt = {
  nyckel: string;
  ursprung: 'mote' | 'paminnelse';
  text: string;
  kund_id: string | null;
  datum: string;
  etikett: string;
  mote?: Mote;
  paminnelse?: Paminnelse;
};

function frånMote(m: Mote): Punkt {
  return {
    nyckel: `m-${m.mote_id}`,
    ursprung: 'mote',
    text: m.notat,
    kund_id: m.kund_id || null,
    datum: m.nästa_kontakt,
    etikett: AKTIVITETS_ETIKETT[m.typ] || m.typ,
    mote: m,
  };
}

function frånPaminnelse(p: Paminnelse): Punkt {
  return {
    nyckel: `p-${p.paminnelse_id}`,
    ursprung: 'paminnelse',
    text: p.text,
    kund_id: p.kund_id || null,
    datum: p.datum,
    etikett: 'påminnelse',
    paminnelse: p,
  };
}

export function hälsningsfras(): string {
  const timme = new Date().getHours();
  if (timme < 5) return 'God natt';
  if (timme < 10) return 'God morgon';
  if (timme < 12) return 'God förmiddag';
  if (timme < 18) return 'God eftermiddag';
  return 'God kväll';
}

const monoStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
};

const etikettStyle: React.CSSProperties = {
  ...monoStyle,
  fontSize: '11px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: TEMA.inkSoft,
  margin: 0,
};

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '26px 28px',
  marginBottom: '40px',
};

const pillFält: React.CSSProperties = {
  height: '32px',
  boxSizing: 'border-box',
  padding: '0 12px',
  borderRadius: '16px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bgPanel,
  color: TEMA.ink,
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  outline: 'none',
};

const textKnappStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: "'Inter', sans-serif",
  fontSize: '13px',
  color: TEMA.inkSoft,
};

// Linjen som sveper fram under en nyss tillagd rad.
function Svep() {
  return (
    <span
      aria-hidden="true"
      style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, height: '1px',
        background: TEMA.accent, transformOrigin: 'left center',
        animation: 'vinsalesSvep 1.3s ease-out forwards',
      }}
    />
  );
}

function datumKnappStyle(vald: boolean): React.CSSProperties {
  return {
    height: '32px',
    padding: '0 14px',
    borderRadius: '16px',
    cursor: 'pointer',
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    fontWeight: vald ? 500 : 400,
    border: `1px solid ${vald ? TEMA.accent : TEMA.border}`,
    background: vald ? TEMA.accent : 'transparent',
    color: vald ? TEMA.onAccent : TEMA.inkSoft,
  };
}

export default function Idag() {
  const [kunder, setKunder] = useState<Kund[]>([]);
  const [moten, setMoten] = useState<Mote[]>([]);
  const [laddar, setLaddar] = useState(true);
  const [fel, setFel] = useState<string | null>(null);
  const navigate = useNavigate();
  const { profil } = useAuth();

  const [paminnelser, setPaminnelser] = useState<Paminnelse[]>([]);
  const [nyligenAvklarade, setNyligenAvklarade] = useState<Punkt[]>([]);
  const [avklaradeÖppen, setAvklaradeÖppen] = useState(false);
  const [uppdaterarId, setUppdaterarId] = useState<string | null>(null);
  const [visaAllaKommande, setVisaAllaKommande] = useState(false);
  const ärMobil = useMobil();

  // Snabbtillägget: en rad i vila, som växer när man klickar i den.
  const [qaÖppen, setQaÖppen] = useState(false);
  const [qaText, setQaText] = useState('');
  const [qaKund, setQaKund] = useState<Kund | null>(null);
  const [qaKundSök, setQaKundSök] = useState('');
  const [qaDatumVal, setQaDatumVal] = useState<DatumVal>('imorgon');
  const [qaEgetDatum, setQaEgetDatum] = useState('');
  const [qaSparar, setQaSparar] = useState(false);
  const [qaKvittens, setQaKvittens] = useState<string | null>(null);
  const [sistTillagd, setSistTillagd] = useState<Paminnelse | null>(null);
  // Nyckeln blinkar till en kort stund, så att man ser var raden hamnade.
  const [nyssTillagdNyckel, setNyssTillagdNyckel] = useState<string | null>(null);
  const [hovradNyckel, setHovradNyckel] = useState<string | null>(null);
  const [sistBorttagen, setSistBorttagen] = useState<Paminnelse | null>(null);
  const [utgåendeNyckel, setUtgåendeNyckel] = useState<string | null>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const kvittensTimer = useRef<number | null>(null);
  const borttagenTimer = useRef<number | null>(null);

  useEffect(() => {
    setLaddar(true);
    setFel(null);
    Promise.all([hämtaKunder(), hämtaMoten(), hämtaPaminnelser()])
      .then(([k, m, p]) => {
        setKunder(k.data);
        setMoten(m.data);
        setPaminnelser(p.data);
        setLaddar(false);
      })
      .catch((err) => {
        console.error('Kunde inte ladda startsidan:', err);
        setFel('Kunde inte ladda din översikt just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, []);

  const idag = dagensDatumLokalt();
  const sistaKommandeDag = datumPlusDagar(idag, KOMMANDE_DAGAR);

  const minaKundIds = new Set(
    moten
      .filter(m => m.säljare === profil?.namn)
      .map(m => m.kund_id)
  );

  const minaKunder = kunder.filter(k => k.aktiv && minaKundIds.has(k.kund_id));

  const minaÖppna = moten.filter(m => m.säljare === profil?.namn && !m.avklarad && m.nästa_kontakt);

  const öppnaPunkter: Punkt[] = [
    ...minaÖppna.map(frånMote),
    ...paminnelser.filter(p => !p.klar).map(frånPaminnelse),
  ];

  // Att göra: förfallna och dagens punkter — det enda som bockas av.
  const attGöraLista = öppnaPunkter
    .filter(p => p.datum <= idag)
    .sort((a, b) => a.datum.localeCompare(b.datum));

  // Kommande: lugn överblick över de närmaste två veckorna.
  const kommandeLista = öppnaPunkter
    .filter(p => p.datum > idag && p.datum <= sistaKommandeDag)
    .sort((a, b) => a.datum.localeCompare(b.datum));

  const synligaKommande = visaAllaKommande
    ? kommandeLista
    : kommandeLista.slice(0, KOMMANDE_VISA_STANDARD);

  const kundFörslag = (() => {
    const s = qaKundSök.trim().toLowerCase();
    if (!s) return [];
    return kunder
      .filter(k => k.aktiv && (
        (k.namn || '').toLowerCase().includes(s) ||
        (k.fortnox_kund_nr || '').toLowerCase().includes(s)
      ))
      .sort((a, b) => a.namn.localeCompare(b.namn, 'sv'))
      .slice(0, MAX_KUNDFÖRSLAG);
  })();

  function valtDatum(): string {
    switch (qaDatumVal) {
      case 'idag': return idag;
      case 'imorgon': return datumPlusDagar(idag, 1);
      default: return qaEgetDatum;
    }
  }

  function stängSnabbtillägg() {
    setQaÖppen(false);
    setQaText('');
    setQaKund(null);
    setQaKundSök('');
    setQaDatumVal('imorgon');
    setQaEgetDatum('');
  }

  // Sparar en aktivitet med ändrade fält. Provningarna hämtas först
  // eftersom PUT ersätter hela aktiviteten inklusive dess provningar.
  async function sparaMote(m: Mote, ändringar: Partial<Mote>) {
    const full = await hämtaMote(m.mote_id);
    const s = { ...m, ...ändringar };
    await uppdateraMote(m.mote_id, {
      kund_id: s.kund_id,
      inkopare_id: s.inkopare_id || null,
      säljare: s.säljare,
      datum: s.datum,
      nästa_kontakt: s.nästa_kontakt || null,
      notat: s.notat,
      typ: s.typ,
      granskad: s.granskad,
      avklarad: s.avklarad,
      provningar: full.data.provningar || [],
    });
  }

  async function bockaAv(p: Punkt) {
    setUppdaterarId(p.nyckel);
    try {
      if (p.ursprung === 'mote' && p.mote) {
        await sparaMote(p.mote, { avklarad: true });
        setMoten(prev => prev.filter(x => x.mote_id !== p.mote!.mote_id));
      } else if (p.paminnelse) {
        await uppdateraPaminnelse(p.paminnelse.paminnelse_id, { klar: true });
        setPaminnelser(prev => prev.filter(x => x.paminnelse_id !== p.paminnelse!.paminnelse_id));
      }
      setNyligenAvklarade(prev => [p, ...prev]);
    } catch (err) {
      console.error('Kunde inte bocka av:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setUppdaterarId(null);
    }
  }

  async function bockaUpp(p: Punkt) {
    setUppdaterarId(p.nyckel);
    try {
      if (p.ursprung === 'mote' && p.mote) {
        await sparaMote(p.mote, { avklarad: false });
        setMoten(prev => [{ ...p.mote!, avklarad: false }, ...prev]);
      } else if (p.paminnelse) {
        await uppdateraPaminnelse(p.paminnelse.paminnelse_id, { klar: false });
        setPaminnelser(prev => [{ ...p.paminnelse!, klar: false }, ...prev]);
      }
      setNyligenAvklarade(prev => prev.filter(x => x.nyckel !== p.nyckel));
    } catch (err) {
      console.error('Kunde inte bocka upp:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setUppdaterarId(null);
    }
  }

  async function läggTillPåminnelse() {
    const datum = valtDatum();
    if (!qaText.trim() || !datum || qaSparar) return;
    setQaSparar(true);
    setQaKvittens(null);
    try {
      const skapad = await skapaPaminnelse({
        text: qaText.trim(),
        datum,
        kund_id: qaKund?.kund_id || null,
      });
      setPaminnelser(prev => [...prev, skapad.data]);
      setSistTillagd(skapad.data);
      setNyssTillagdNyckel(`p-${skapad.data.paminnelse_id}`);
      window.setTimeout(() => setNyssTillagdNyckel(null), 2200);
      setQaText('');
      setQaKund(null);
      setQaKundSök('');
      setQaDatumVal('imorgon');
      setQaEgetDatum('');
      setQaKvittens(datum === idag ? 'Tillagd i dagens lista' : `Tillagd för ${fulltKortDatum(datum)}`);
      // Kvittensen är tillfällig — listan visar ju redan att raden finns.
      if (kvittensTimer.current) window.clearTimeout(kvittensTimer.current);
      kvittensTimer.current = window.setTimeout(() => {
        setQaKvittens(null);
        setSistTillagd(null);
      }, 6000);
      // Raden står kvar öppen, så att nästa påminnelse kan skrivas direkt.
      textRef.current?.focus();
    } catch (err) {
      console.error('Kunde inte spara påminnelsen:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setQaSparar(false);
    }
  }

  async function ångraSenaste() {
    if (!sistTillagd) return;
    const id = sistTillagd.paminnelse_id;
    setSistTillagd(null);
    setQaKvittens(null);
    setPaminnelser(prev => prev.filter(x => x.paminnelse_id !== id));
    try {
      await taBortPaminnelse(id);
    } catch (err) {
      console.error('Kunde inte ångra:', err);
      alert('Kunde inte ångra. Ladda om sidan och försök igen.');
    }
  }

  // Bara påminnelser går att ta bort härifrån. Uppföljningar hör ihop med en
  // aktivitet och tas bort på kundsidan, där historiken syns.
  async function taBortKommande(p: Punkt) {
    if (!p.paminnelse || utgåendeNyckel) return;
    const borttagen = p.paminnelse;
    // Låt raden animeras ut innan den plockas bort ur listan.
    setUtgåendeNyckel(p.nyckel);
    await new Promise(r => window.setTimeout(r, 220));
    setUtgåendeNyckel(null);
    setPaminnelser(prev => prev.filter(x => x.paminnelse_id !== borttagen.paminnelse_id));
    setSistBorttagen(borttagen);
    if (borttagenTimer.current) window.clearTimeout(borttagenTimer.current);
    borttagenTimer.current = window.setTimeout(() => setSistBorttagen(null), 6000);
    try {
      await taBortPaminnelse(borttagen.paminnelse_id);
    } catch (err) {
      console.error('Kunde inte ta bort:', err);
      setPaminnelser(prev => [...prev, borttagen]);
      setSistBorttagen(null);
      alert('Kunde inte ta bort. Försök igen.');
    }
  }

  async function ångraBorttagning() {
    if (!sistBorttagen) return;
    const gammal = sistBorttagen;
    setSistBorttagen(null);
    try {
      const skapad = await skapaPaminnelse({
        text: gammal.text,
        datum: gammal.datum,
        kund_id: gammal.kund_id,
      });
      setPaminnelser(prev => [...prev, skapad.data]);
      setNyssTillagdNyckel(`p-${skapad.data.paminnelse_id}`);
      window.setTimeout(() => setNyssTillagdNyckel(null), 2200);
    } catch (err) {
      console.error('Kunde inte ångra borttagningen:', err);
      alert('Kunde inte ångra. Ladda om sidan och försök igen.');
    }
  }

  const senasteMötenLista = [...moten]
    .filter(m => (m.typ === 'möte' || m.typ === 'epost') && minaKunder.some(k => k.kund_id === m.kund_id))
    .sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime())
    .slice(0, 20);

  const idagRubrik = new Date()
    .toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' })
    .toUpperCase();

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
    </div>
  );

  if (fel) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  const nästaKommande = kommandeLista[0];
  const nästaKund = nästaKommande ? kunder.find(k => k.kund_id === nästaKommande.kund_id) : undefined;
  const kanSpara = !!qaText.trim() && !!valtDatum() && !qaSparar;

  return (
    <div>
      <style>{`
        /* Ny rad: texten tonar in, och en tunn linje sveper fram under raden. */
        @keyframes vinsalesNy { from { opacity: 0; } to { opacity: 1; } }
        @keyframes vinsalesSvep {
          0%   { transform: scaleX(0); opacity: 1; }
          35%  { transform: scaleX(1); opacity: 1; }
          100% { transform: scaleX(1); opacity: 0; }
        }
        /* Borttagen rad: glider undan och kollapsar ihop. */
        @keyframes vinsalesBort {
          from { opacity: 1; transform: none;               max-height: 80px; }
          to   { opacity: 0; transform: translateX(10px);   max-height: 0; }
        }
      `}</style>
      <div style={{ marginBottom: '36px' }}>
        <p style={{ ...etikettStyle, color: TEMA.accent }}>{idagRubrik}</p>
        <h1 style={{ margin: '10px 0 0', fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>
          {/* Hälsningen står på startsidan sedan 3 nov 2026; här är det listan. */}
          Att göra
        </h1>
      </div>

      <div style={ärMobil ? { ...kortStyle, padding: '20px 16px', marginBottom: '32px' } : kortStyle}>
        {/* ───────── Att göra ───────── */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: '19px', margin: 0, color: TEMA.ink }}>Att göra</h2>
          {attGöraLista.length > 0 && (
            <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint }}>{attGöraLista.length} kvar</span>
          )}
        </div>

        <div style={{ marginTop: '8px' }}>
          {attGöraLista.length === 0 && (
            <p style={{ color: TEMA.inkSoft, fontSize: '14px', fontFamily: "'Inter', sans-serif", margin: 0, padding: '10px 0 6px' }}>
              Inget inplanerat i dag.
              {nästaKommande && (
                <span style={{ color: TEMA.inkFaint }}>
                  {' '}Nästa: {fulltKortDatum(nästaKommande.datum)}, {nästaKund?.namn || nästaKommande.text}.
                </span>
              )}
            </p>
          )}
          {attGöraLista.map(punkt => {
            const kund = kunder.find(k => k.kund_id === punkt.kund_id);
            const etikett = närEtikett(punkt.datum, idag);
            const arbetar = uppdaterarId === punkt.nyckel;
            return (
              <div key={punkt.nyckel} style={{
                display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '13px 0',
                borderBottom: `1px solid ${TEMA.border}`,
                position: 'relative',
                animation: nyssTillagdNyckel === punkt.nyckel ? 'vinsalesNy 0.18s ease-out' : undefined,
              }}>
                {nyssTillagdNyckel === punkt.nyckel && <Svep />}
                <button
                  onClick={() => !arbetar && bockaAv(punkt)}
                  aria-label="Bocka av"
                  style={{
                    width: '18px', height: '18px', borderRadius: '50%', marginTop: '1px', flexShrink: 0, padding: 0,
                    background: 'transparent',
                    border: `1.5px solid ${etikett.overdue || etikett.today ? TEMA.accent : TEMA.inkFaint}`,
                    cursor: arbetar ? 'default' : 'pointer',
                    opacity: arbetar ? 0.5 : 1,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '14.5px', color: TEMA.ink }}>{punkt.text}</p>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                    {kund && <span style={{ color: TEMA.inkSoft }}>{kund.namn}</span>}
                    {kund && ' · '}
                    {punkt.etikett}
                  </p>
                </div>
                {!etikett.today && (
                  <span style={{
                    flexShrink: 0, ...monoStyle, fontSize: '11px',
                    padding: '3px 9px', borderRadius: '2px', whiteSpace: 'nowrap',
                    border: `1px solid ${etikett.overdue ? '#D9695F' : TEMA.border}`,
                    color: etikett.overdue ? '#D9695F' : TEMA.inkSoft,
                  }}>
                    {etikett.text}
                  </span>
                )}
              </div>
            );
          })}

          {/* ───────── Snabbtillägg: listans sista rad ───────── */}
          <div
            onBlur={e => {
              // Stäng när fokus lämnar raden och inget är skrivet.
              if (!e.currentTarget.contains(e.relatedTarget as Node) && !qaText.trim()) {
                setQaÖppen(false);
              }
            }}
            style={{
              margin: '10px -14px 0', padding: '10px 14px', borderRadius: '6px',
              border: `1px solid ${qaÖppen ? TEMA.border : 'transparent'}`,
              background: qaÖppen ? TEMA.bg : 'transparent',
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <button
                onClick={() => textRef.current?.focus()}
                aria-label="Lägg till en påminnelse"
                tabIndex={-1}
                style={{
                  width: '18px', padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                  textAlign: 'center', color: TEMA.accent, fontSize: '19px', lineHeight: 1, flexShrink: 0,
                }}
              >
                +
              </button>
              <input
                ref={textRef}
                type="text"
                value={qaText}
                aria-label="Lägg till en påminnelse"
                placeholder="Lägg till en påminnelse…"
                onFocus={() => setQaÖppen(true)}
                onChange={e => { setQaText(e.target.value); setQaKvittens(null); }}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); läggTillPåminnelse(); }
                  if (e.key === 'Escape') { stängSnabbtillägg(); (e.target as HTMLInputElement).blur(); }
                }}
                style={{
                  flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
                  color: TEMA.ink, fontFamily: "'Inter', sans-serif", fontSize: '14.5px', padding: '6px 0',
                }}
              />
            </div>

            {qaÖppen && !qaText.trim() && (
              <p style={{ margin: '4px 0 2px 32px', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                Skriv vad du vill komma ihåg. Kund och datum väljer du sen.
              </p>
            )}

            {qaÖppen && !!qaText.trim() && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '10px 0 2px 32px' }}>
                {/* Kund, sökbar. På mobil en egen rad över datumknapparna. */}
                <div style={{ position: 'relative', flex: ärMobil ? '1 1 100%' : undefined }}>
                  {qaKund ? (
                    <button
                      onClick={() => setQaKund(null)}
                      title="Ta bort kund"
                      style={{ ...pillFält, cursor: 'pointer', border: `1px solid ${TEMA.accent}`, color: TEMA.accent, background: 'rgba(224,168,62,0.08)' }}
                    >
                      {qaKund.namn} ×
                    </button>
                  ) : (
                    <input
                      type="text"
                      value={qaKundSök}
                      aria-label="Kund"
                      placeholder="Koppla till kund (valfritt)"
                      onChange={e => setQaKundSök(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && kundFörslag.length > 0) {
                          e.preventDefault();
                          setQaKund(kundFörslag[0]);
                          setQaKundSök('');
                          textRef.current?.focus();
                        }
                        if (e.key === 'Escape') setQaKundSök('');
                      }}
                      style={{ ...pillFält, width: ärMobil ? '100%' : '210px' }}
                    />
                  )}
                  {!qaKund && kundFörslag.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '38px', left: 0, width: 'min(300px, calc(100vw - 64px))', zIndex: 20,
                      background: TEMA.bgPanel, border: `1px solid ${TEMA.border}`, borderRadius: '6px',
                      boxShadow: '0 12px 28px rgba(0,0,0,0.45)', overflow: 'hidden',
                    }}>
                      {kundFörslag.map(k => (
                        <button
                          key={k.kund_id}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => { setQaKund(k); setQaKundSök(''); textRef.current?.focus(); }}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px',
                            border: 'none', background: 'transparent', cursor: 'pointer',
                            fontFamily: "'Inter', sans-serif", fontSize: '13.5px', color: TEMA.ink,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = TEMA.bg; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {k.namn}
                          {k.stad && <span style={{ color: TEMA.inkFaint }}> · {k.stad}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {!ärMobil && (
                  <span aria-hidden="true" style={{ width: '1px', height: '20px', background: TEMA.border, margin: '0 4px' }} />
                )}

                {/* Datum */}
                {DATUMKNAPPAR.map(d => (
                  <button
                    key={d.val}
                    onClick={() => setQaDatumVal(d.val)}
                    style={datumKnappStyle(qaDatumVal === d.val)}
                  >
                    {d.etikett}
                  </button>
                ))}
                {qaDatumVal === 'valj' && (
                  <input
                    type="date"
                    value={qaEgetDatum}
                    min={idag}
                    aria-label="Datum"
                    onChange={e => setQaEgetDatum(e.target.value)}
                    style={{ ...pillFält, ...monoStyle }}
                  />
                )}

                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {!ärMobil && (
                    <span style={{ ...monoStyle, fontSize: '11px', color: TEMA.inkFaint }}>Enter sparar · Esc stänger</span>
                  )}
                  <button
                    onClick={läggTillPåminnelse}
                    disabled={!kanSpara}
                    style={{
                      height: '32px', padding: '0 16px', borderRadius: '2px',
                      background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
                      fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
                      cursor: kanSpara ? 'pointer' : 'default', opacity: kanSpara ? 1 : 0.5,
                    }}
                  >
                    {qaSparar ? 'Sparar...' : 'Lägg till'}
                  </button>
                </span>
              </div>
            )}
          </div>

          {/* Dämpad och tillfällig: grönt är reserverat för avklarat. */}
          {qaKvittens && (
            <p style={{
              margin: '8px 0 0 32px', display: 'flex', alignItems: 'center', gap: '10px',
              fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif",
              animation: 'vinsalesNy 0.18s ease-out',
            }}>
              {qaKvittens}
              {sistTillagd && (
                <button
                  onClick={ångraSenaste}
                  style={{ ...textKnappStyle, fontSize: '12.5px', color: TEMA.accent, textDecoration: 'underline' }}
                >
                  Ångra
                </button>
              )}
            </p>
          )}
        </div>

        {nyligenAvklarade.length > 0 && (
          <div
            onClick={() => setAvklaradeÖppen(o => !o)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '9px', cursor: 'pointer',
              margin: '14px 0 2px', padding: '8px 14px 8px 10px', borderRadius: '20px',
              background: 'rgba(95,174,138,0.09)', border: '1px solid rgba(95,174,138,0.25)',
              fontFamily: "'Inter', sans-serif", fontSize: '12.5px', fontWeight: 500, color: TEMA.positive,
              userSelect: 'none',
            }}
          >
            <span style={{
              width: '16px', height: '16px', borderRadius: '50%', background: TEMA.positive,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="8" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke={TEMA.bg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span>{nyligenAvklarade.length} avklarade idag</span>
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ transform: avklaradeÖppen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease', opacity: 0.8 }}>
              <path d="M2 1L6 4L2 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}

        {avklaradeÖppen && nyligenAvklarade.map((punkt, idx) => {
          const kund = kunder.find(k => k.kund_id === punkt.kund_id);
          return (
            <div key={punkt.nyckel} style={{
              display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '13px 0',
              borderTop: `1px solid ${TEMA.border}`, marginTop: idx === 0 ? '6px' : 0,
            }}>
              <div
                onClick={() => uppdaterarId !== punkt.nyckel && bockaUpp(punkt)}
                style={{
                  width: '16px', height: '16px', borderRadius: '50%', marginTop: '2px', flexShrink: 0,
                  background: TEMA.positive, border: `1.5px solid ${TEMA.positive}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: uppdaterarId === punkt.nyckel ? 'default' : 'pointer',
                  opacity: uppdaterarId === punkt.nyckel ? 0.5 : 1,
                }}
              >
                <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke={TEMA.bg} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, textDecoration: 'line-through' }}>{punkt.text}</p>
                <p style={{ margin: '3px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif", opacity: 0.6 }}>
                  {kund && `${kund.namn} · `}{punkt.etikett}
                </p>
              </div>
            </div>
          );
        })}

        {/* ───────── Kommande, grupperat per dag ───────── */}
        {kommandeLista.length > 0 && (
          <div style={{ marginTop: '30px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
              <span style={{ ...monoStyle, fontSize: '10.5px', letterSpacing: '0.12em', color: TEMA.inkFaint }}>KOMMANDE</span>
              <div style={{ flex: 1, height: '1px', background: TEMA.border }} />
            </div>

            {synligaKommande.map((punkt, idx) => {
              const kund = kunder.find(k => k.kund_id === punkt.kund_id);
              const klickbar = !!kund;
              const nyDag = idx === 0 || synligaKommande[idx - 1].datum !== punkt.datum;
              return (
                <div
                  key={punkt.nyckel}
                  onMouseEnter={() => setHovradNyckel(punkt.nyckel)}
                  onMouseLeave={() => setHovradNyckel(null)}
                  onClick={() => kund && navigate(`/kund/${kund.kund_id}`)}
                  style={{
                    // På mobil staplas datum, text och kund, i stället för tre kolumner
                    // där texten pressas till ett ord per rad.
                    display: 'flex', alignItems: 'baseline', gap: ärMobil ? '2px 12px' : '16px',
                    flexWrap: ärMobil ? 'wrap' : undefined,
                    padding: ärMobil && punkt.ursprung === 'paminnelse' ? '8px 40px 8px 10px' : '8px 10px', margin: `${nyDag && idx > 0 ? '6px' : '0'} -10px 0`,
                    borderRadius: '5px',
                    position: 'relative', overflow: 'hidden',
                    background: hovradNyckel === punkt.nyckel && utgåendeNyckel !== punkt.nyckel
                      ? 'rgba(255,255,255,0.03)' : 'transparent',
                    transition: 'background 0.12s ease',
                    animation: utgåendeNyckel === punkt.nyckel
                      ? 'vinsalesBort 0.22s ease-in forwards'
                      : nyssTillagdNyckel === punkt.nyckel ? 'vinsalesNy 0.18s ease-out' : undefined,
                    cursor: klickbar ? 'pointer' : 'default',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {nyssTillagdNyckel === punkt.nyckel && <Svep />}
                  {(!ärMobil || nyDag) && (
                    <span style={{
                      ...monoStyle, fontSize: '12px', color: TEMA.inkFaint, flexShrink: 0,
                      width: ärMobil ? '100%' : '92px', marginBottom: ärMobil ? '2px' : undefined,
                    }}>
                      {nyDag ? fulltKortDatum(punkt.datum) : ''}
                    </span>
                  )}
                  <span style={{ fontSize: '13.5px', color: TEMA.inkSoft, minWidth: 0, flex: ärMobil ? '1 1 100%' : undefined }}>{punkt.text}</span>
                  {kund && (
                    <span style={{
                      fontSize: '12.5px', color: TEMA.inkFaint,
                      whiteSpace: ärMobil ? 'normal' : 'nowrap', minWidth: 0,
                    }}>{kund.namn}</span>
                  )}
                  {punkt.ursprung === 'paminnelse' && (
                    <button
                      onClick={e => { e.stopPropagation(); taBortKommande(punkt); }}
                      aria-label="Ta bort påminnelsen"
                      title="Ta bort"
                      style={{
                        ...(ärMobil ? { position: 'absolute', right: '8px', top: '6px' } : {}),
                        marginLeft: 'auto', flexShrink: 0, alignSelf: 'center',
                        width: '24px', height: '24px', padding: 0, borderRadius: '50%',
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: TEMA.inkFaint,
                        opacity: ärMobil || hovradNyckel === punkt.nyckel ? 1 : 0,
                        transition: 'opacity 0.12s ease, background 0.12s ease, color 0.12s ease',
                      }}
                      onFocus={() => setHovradNyckel(punkt.nyckel)}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = 'rgba(217,105,95,0.14)';
                        e.currentTarget.style.color = '#D9695F';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = TEMA.inkFaint;
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M2.5 3.5h9M5.5 3.5V2.4c0-.5.4-.9.9-.9h1.2c.5 0 .9.4.9.9v1.1M3.6 3.5l.5 8c0 .6.5 1 1 1h3.8c.5 0 1-.4 1-1l.5-8"
                              stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}

            {sistBorttagen && (
              <p style={{
                margin: '12px 0 0', display: 'flex', alignItems: 'center', gap: '10px',
                fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif",
              }}>
                <span style={{ textDecoration: 'line-through' }}>{sistBorttagen.text}</span>
                borttagen
                <button
                  onClick={ångraBorttagning}
                  style={{ ...textKnappStyle, fontSize: '12.5px', color: TEMA.accent, textDecoration: 'underline' }}
                >
                  Ångra
                </button>
              </p>
            )}

            {kommandeLista.length > KOMMANDE_VISA_STANDARD && (
              <button
                onClick={() => setVisaAllaKommande(v => !v)}
                style={{ ...textKnappStyle, marginTop: '6px' }}
              >
                {visaAllaKommande ? 'Visa färre' : `Visa alla (${kommandeLista.length})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ───────── Senaste kundbesök ───────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '18px' }}>
        <p style={{ ...etikettStyle, color: TEMA.accent }}>Senast genomförda kundbesök</p>
        <button
          onClick={() => navigate('/registrera')}
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '14px',
            fontWeight: 500,
            padding: '10px 20px',
            borderRadius: '2px',
            background: TEMA.accent,
            color: TEMA.onAccent,
            border: `1px solid ${TEMA.accent}`,
            cursor: 'pointer',
            transition: 'background 0.15s ease',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = TEMA.accentHover; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = TEMA.accent; }}
        >
          + Registrera aktivitet
        </button>
      </div>

      {senasteMötenLista.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>
          Inga möten registrerade ännu.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {senasteMötenLista.map(m => {
          const kund = kunder.find(k => k.kund_id === m.kund_id);
          return (
            <div
              key={m.mote_id}
              onClick={() => navigate(`/kund/${m.kund_id}`)}
              style={{
                background: TEMA.bgPanel,
                border: `1px solid ${TEMA.border}`,
                borderRadius: '8px',
                padding: ärMobil ? '14px 14px' : '18px 22px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                transition: 'border-color 0.15s ease',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.inkSoft; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = TEMA.border; }}
            >
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                background: TEMA.inkFaint,
              }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontFamily: "'Fraunces', serif", fontSize: '16px', color: TEMA.ink }}>{kund?.namn || m.kund_id}</span>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>
                  {m.säljare}{m.notat ? ` · ${m.notat}` : ''}
                </p>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0, fontFamily: "'Inter', sans-serif" }}>
                <p style={{ margin: 0, fontSize: '13px', color: TEMA.inkFaint }}>{m.datum}</p>
                {m.nästa_kontakt && (
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: TEMA.inkSoft }}>
                    Nästa: {m.nästa_kontakt}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
